import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, UserX, Save } from "lucide-react";

export default function Profilo() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ first_name: "", last_name: "", phone: "" });
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync form only when user changes; setForm is a stable setter
  useEffect(() => { if (user) setForm({ first_name: user.first_name, last_name: user.last_name, phone: user.phone || "" }); }, [user]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/patient/profile", form); await refresh(); toast.success("Profilo aggiornato."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "etruriacare-dati.json"; a.click();
      toast.success("Dati esportati.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deactivate = async () => {
    if (!window.confirm("Sei sicuro di voler disattivare il tuo account?")) return;
    try { await api.post("/account/deactivate"); toast.success("Account disattivato."); window.location.href = "/login"; }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Profilo">
      <div className="max-w-2xl space-y-6">
        <Card className="space-y-4">
          <h3 className="font-bold text-[#102A2A]">Dati personali</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Nome</Label><Input data-testid="profile-firstname" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
            <div><Label>Cognome</Label><Input data-testid="profile-lastname" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
          </div>
          <div><Label>Email</Label><Input value={user?.email || ""} disabled className="h-12 rounded-xl mt-1 bg-[#EAF3EF]" /></div>
          <div><Label>Telefono</Label><Input data-testid="profile-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 rounded-xl mt-1" /></div>
          <Button data-testid="profile-save" onClick={save} disabled={saving} className="bg-[#087A73] text-white rounded-xl h-12 px-6"><Save className="mr-2 h-4 w-4" /> Salva modifiche</Button>
        </Card>

        <Card className="space-y-4">
          <h3 className="font-bold text-[#102A2A]">Privacy e dati (GDPR)</h3>
          <p className="text-sm text-[#5C6B68]">Puoi esportare i tuoi dati non clinici o disattivare l'account in qualsiasi momento.</p>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="export-data" variant="outline" onClick={exportData} className="rounded-xl h-12 px-5 border-2 border-[#087A73] text-[#087A73]"><Download className="mr-2 h-4 w-4" /> Esporta i miei dati</Button>
            <Button data-testid="deactivate-account" variant="outline" onClick={deactivate} className="rounded-xl h-12 px-5 border-2 border-[#C0392B]/30 text-[#C0392B]"><UserX className="mr-2 h-4 w-4" /> Disattiva account</Button>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
